const API_URL = process.env.REACT_APP_BACKEND_URL;
const isDev = process.env.NODE_ENV === 'development';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function subscribeToPushNotifications(token) {
  try {
    // Check if push notifications are supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      if (isDev) console.log('Push notifications not supported');
      return { success: false, reason: 'not_supported' };
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      if (isDev) console.log('Notification permission denied');
      return { success: false, reason: 'permission_denied' };
    }

    // Wait for service worker to be ready
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key from server
    const vapidResponse = await fetch(`${API_URL}/api/push/vapid-key`);
    if (!vapidResponse.ok) {
      throw new Error('Failed to get VAPID key');
    }
    const { publicKey } = await vapidResponse.json();

    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    // Send subscription to server
    const subscriptionJson = subscription.toJSON();
    const response = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      })
    });

    if (response.ok) {
      if (isDev) console.log('Successfully subscribed to push notifications');
      return { success: true };
    } else {
      throw new Error('Failed to save subscription on server');
    }
  } catch (error) {
    if (isDev) console.error('Error subscribing to push notifications:', error);
    return { success: false, reason: 'error', error };
  }
}

export async function unsubscribeFromPushNotifications(token) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe locally
      await subscription.unsubscribe();
      
      // Remove from server
      const subscriptionJson = subscription.toJSON();
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        })
      });
    }
    
    return { success: true };
  } catch (error) {
    if (isDev) console.error('Error unsubscribing from push notifications:', error);
    return { success: false, error };
  }
}

export async function checkPushSubscription() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return { supported: false, subscribed: false };
    }
    
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    return {
      supported: true,
      subscribed: !!subscription,
      permission: Notification.permission
    };
  } catch (error) {
    if (isDev) console.error('Error checking push subscription:', error);
    return { supported: false, subscribed: false, error };
  }
}
